import React, { useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  EditIcon,
  Fade,
  Heading,
  IconButton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableHeaderRow,
  TableRow,
  Text,
  PlusIcon,
  TrashIcon,
} from '@razorpay/blade/components';
import { useFormWithProvider } from 'forms';

import type { LeaveType } from 'api/types/attendanceSettingsTypes';
import type { AttendanceSettingsFormState } from 'components/Settings/AttendanceAndLeavesSettings/types';
import { AddLeaveTypeModal } from './AddLeaveTypeModal';
import { DeleteConfirmModal } from 'components/Settings/AttendanceAndLeavesSettings/components/DeleteConfirmModal';
import { uniqueId } from 'utils/Numbers';

type LeaveTypeWithId = LeaveType & { id: string };

export const LeaveTypesTable = () => {
  const { formData, setFieldValue, errors } = useFormWithProvider<AttendanceSettingsFormState>();
  const leaveTypes = formData.leaveTypes;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingIdx, setEditingIdx] = useState<number | null>(null);
  const [pendingDeleteIdx, setPendingDeleteIdx] = useState<number | null>(null);
  const addCountRef = useRef(0);

  const setLeaveTypes = (next: LeaveType[]) => setFieldValue('leaveTypes', next);

  const updateRow = (idx: number, updated: LeaveType) => {
    setLeaveTypes(leaveTypes?.map((row, i) => (i === idx ? updated : row)) || []);
  };

  const removeRow = (idx: number) => {
    setLeaveTypes(leaveTypes?.filter((_, i) => i !== idx) || []);
  };

  const openAddModal = () => {
    setEditingIdx(null);
    setIsModalOpen(true);
  };

  const openEditModal = (idx: number) => {
    setEditingIdx(idx);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingIdx(null);
  };

  const handleModalSave = (row: LeaveType) => {
    if (editingIdx !== null) {
      updateRow(editingIdx, row);
    } else {
      addCountRef.current += 1;
      setLeaveTypes([...(leaveTypes ?? []), row]);
    }
    closeModal();
  };

  const rows: LeaveTypeWithId[] = leaveTypes?.map((leaveType) => ({ ...leaveType, id: uniqueId() })) || [];

  return (
    <Box
      padding="spacing.6"
      borderRadius="medium"
      borderWidth="thin"
      borderColor="surface.border.gray.subtle"
      backgroundColor="surface.background.gray.intense">
      <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap="spacing.6">
        <Box>
          <Heading size="medium">Leave types</Heading>
          <Text marginTop="spacing.2" size="medium" color="surface.text.gray.muted">
            Each leave type has its own accrual and balance rules.
          </Text>
        </Box>
        <Button variant="secondary" icon={PlusIcon} iconPosition="left" onClick={openAddModal}>
          Add Leave Type
        </Button>
      </Box>

      {errors.leaveTypes && (
        <Box marginTop="spacing.5">
          <Alert color="negative" description={errors.leaveTypes} isFullWidth />
        </Box>
      )}

      <Fade key={addCountRef.current} motionTriggers={['mount']} type="in">
        <Box marginTop="spacing.7">
          <Table
            data={{ nodes: rows }}
            rowDensity="comfortable"
            gridTemplateColumns="2fr 1fr 1fr 1fr 1fr auto auto">
            {(tableData) => (
              <>
                <TableHeader>
                  <TableHeaderRow>
                    <TableHeaderCell>Type</TableHeaderCell>
                    <TableHeaderCell>Default</TableHeaderCell>
                    <TableHeaderCell>Increment</TableHeaderCell>
                    <TableHeaderCell>Max Leave</TableHeaderCell>
                    <TableHeaderCell>Carry Forward</TableHeaderCell>
                    <TableHeaderCell>Weekends</TableHeaderCell>
                    <TableHeaderCell> </TableHeaderCell>
                  </TableHeaderRow>
                </TableHeader>
                <TableBody>
                  {tableData.map((row: LeaveTypeWithId, idx: number) => {
                    return (
                      <TableRow key={row.id} item={row}>
                        <TableCell>
                          <Text size="medium">{row.name}</Text>
                        </TableCell>

                        <TableCell>
                          <Text size="medium">{row.defaultLeaves}</Text>
                        </TableCell>

                        <TableCell>
                          <Text size="medium">{row.monthlyIncrement}</Text>
                        </TableCell>

                        <TableCell>
                          <Text size="medium">{row.maxLeaves ?? 0}</Text>
                        </TableCell>

                        <TableCell>
                          <Text size="medium">{row.carryForward ?? 0}</Text>
                        </TableCell>

                        <TableCell>
                          <Switch
                            size="medium"
                            accessibilityLabel={`Include weekends for ${row.name || 'leave type'}`}
                            isChecked={row.includeWeekendsAndHolidays}
                            onChange={({ isChecked }) =>
                              updateRow(idx, { ...row, includeWeekendsAndHolidays: isChecked })
                            }
                          />
                        </TableCell>

                        <TableCell>
                          <Box display="flex" gap="spacing.4" alignItems="center">
                            <Box>
                              <IconButton
                                accessibilityLabel={`Edit ${row.name || 'leave type'}`}
                                icon={EditIcon}
                                emphasis="subtle"
                                onClick={() => openEditModal(idx)}
                              />
                            </Box>
                            <IconButton
                              accessibilityLabel={`Remove ${row.name || 'leave type'}`}
                              icon={TrashIcon}
                              emphasis="subtle"
                              onClick={() => setPendingDeleteIdx(idx)}
                            />
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </>
            )}
          </Table>
        </Box>
      </Fade>

      <Text marginTop="spacing.4" size="medium" color="surface.text.gray.muted">
        Changes apply from the next payroll cycle.
      </Text>

      <AddLeaveTypeModal
        isOpen={isModalOpen}
        onDismiss={closeModal}
        onAdd={handleModalSave}
        initialData={editingIdx !== null ? leaveTypes?.[editingIdx] ?? null : null}
      />

      <DeleteConfirmModal
        isOpen={pendingDeleteIdx !== null}
        entityName="this leave type"
        onDismiss={() => setPendingDeleteIdx(null)}
        onConfirm={() => {
          if (pendingDeleteIdx !== null) removeRow(pendingDeleteIdx);
          setPendingDeleteIdx(null);
        }}
      />
    </Box>
  );
};